import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Loader2, Mail, Phone, UploadCloud, X, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { useDropdowns } from "@/hooks/useMeta";
import { canDelete } from "@/lib/constants";
import { DeleteButton } from "@/components/DeleteButton";
import { PageHeader } from "@/components/PageHeader";
import { BulkImport } from "@/components/BulkImport";
import { DivisionTag } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalespersonFilter } from "@/components/SalespersonFilter";
import { AddToBrevoDialog } from "@/components/AddToBrevoDialog";
import { ContactDialog } from "@/components/ContactDialog";
import { ScanCardButton } from "@/components/ScanCardDialog";
import { EventFilter } from "@/components/EventFilter";

export default function Contacts({ dealerView }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { divisionParam, isGlobal } = useDivision();
  const { data: dd } = useDropdowns();
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [event, setEvent] = useState(() => new URLSearchParams(window.location.search).get("event") || "");
  const [followup, setFollowup] = useState(() => new URLSearchParams(window.location.search).get("followup") || "");
  const [type, setType] = useState("");
  const [owner, setOwner] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sel, setSel] = useState([]);
  const [brevoOpen, setBrevoOpen] = useState(false);
  const canPushBrevo = ["marketing", "super_admin"].includes(user?.role);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts", divisionParam, search, type, owner, event, followup, !!dealerView],
    queryFn: async () => (await api.get("/contacts", { params: { division_id: divisionParam, search, type, created_by: owner, event, followup, dealer_view: dealerView ? 1 : 0 } })).data,
  });

  const { data: countData } = useQuery({
    queryKey: ["contacts-count", divisionParam, search, type, event, !!dealerView],
    queryFn: async () => (await api.get("/contacts/summary", { params: { division_id: divisionParam, search, type, event, dealer_view: dealerView ? 1 : 0 } })).data,
  });
  const totalContacts = countData?.total;

  const del = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success("Contact deleted"); qc.invalidateQueries({ queryKey: ["contacts"] }); }
    catch (e) { toast.error(apiError(e)); throw e; }
  };

  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleCalled = async (c) => {
    try {
      await api.post(`/contacts/${c.id}/followup`, { called: !c.followup_called_at });
      qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contacts-count"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const withEmail = contacts.filter((c) => c.email);

  return (
    <div>
      <PageHeader title={dealerView ? "Dealer Contacts" : "Contacts"}
        subtitle={dealerView ? "Contacts inside dealer-added universe companies." : "Architects, consultants, contractors and customers across your deals."}>
        {user?.role !== "marketing" && (
          <>
            {!dealerView && <BulkImport entity="contact" templatePath="/contacts/import-template" importPath="/contacts/import"
              onDone={() => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["contacts-count"] }); }} />}
            {!dealerView && <ScanCardButton />}
            <Button className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-contact-btn"><Plus className="h-4 w-4" /> New Contact</Button>
          </>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid="contacts-total-chip">
          {totalContacts != null ? totalContacts.toLocaleString("en-IN") : "…"} contacts
          {totalContacts != null && contacts.length < totalContacts && (
            <span className="font-normal text-slate-400">· showing {contacts.length.toLocaleString("en-IN")}</span>
          )}
        </span>
        {countData && Object.entries(countData.by_type || {}).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
          <span key={t} className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-medium text-slate-500 whitespace-nowrap" data-testid={`contacts-chip-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>
            <span className="font-semibold text-slate-700 mr-1">{n.toLocaleString("en-IN")}</span>{t}{n > 1 && !t.endsWith("s") ? "s" : ""}
          </span>
        ))}
        {countData && Object.keys(countData.by_role || {}).length > 0 && <span className="h-5 w-px bg-slate-200 mx-0.5" />}
        {countData && Object.entries(countData.by_role || {}).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
          <span key={t} className="inline-flex items-center rounded-md bg-indigo-50 border border-indigo-100 px-2 py-1.5 text-[11px] font-medium text-indigo-700 whitespace-nowrap" data-testid={`contacts-chip-role-${t.replace(/\s+/g, "-").toLowerCase()}`}>
            <span className="font-semibold mr-1">{n.toLocaleString("en-IN")}</span>{t}{n > 1 && !t.endsWith("s") ? "s" : ""}
          </span>
        ))}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts…" className="h-9 pl-9" data-testid="contact-search" />
        </div>
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="contact-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{dd?.contact_type?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <SalespersonFilter value={owner || "all"} onChange={(v) => setOwner(v === "all" ? "" : v)} testid="contact-salesperson-filter" />
        <EventFilter entity="contacts" value={event} onChange={setEvent} divisionParam={divisionParam} testid="contact-event-filter" />
        {(event || followup) && countData?.followup && (
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="contact-followup-toggle">
            {[["", `All ${countData.followup.total}`], ["pending", `To call ${countData.followup.pending}`], ["called", `Called ${countData.followup.called}`]].map(([v, l]) => (
              <button key={v} onClick={() => setFollowup(v)} className={`px-2.5 h-9 ${followup === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`contact-followup-${v || "all"}`}>{l}</button>
            ))}
          </div>
        )}
        {canPushBrevo && withEmail.length > 0 && (
          <>
            <Button variant="outline" size="sm" className="h-9" data-testid="contacts-select-all-btn"
              onClick={() => setSel(sel.length === withEmail.length ? [] : withEmail.map((c) => c.id))}>
              {sel.length === withEmail.length ? "Clear selection" : `Select all (${withEmail.length})`}
            </Button>
            {sel.length > 0 && (
              <Button size="sm" className="h-9 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => setBrevoOpen(true)} data-testid="add-to-brevo-btn">
                <UploadCloud className="h-4 w-4" /> Add to Brevo list ({sel.length})
              </Button>
            )}
          </>
        )}
      </div>

      {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contacts.map((c) => (
            <Card key={c.id} className={`p-4 hover:shadow transition-shadow cursor-pointer ${sel.includes(c.id) ? "ring-2 ring-slate-900/40" : ""}`} onClick={() => { setEditing(c); setOpen(true); }} data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  {canPushBrevo && c.email && (
                    <input type="checkbox" checked={sel.includes(c.id)} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); toggleSel(c.id); }} className="mt-1 accent-slate-900"
                      data-testid={`contact-select-${c.id}`} />
                  )}
                  <div>
                    <p className={`font-medium ${(c.name || "").trim() ? "text-slate-900" : "text-amber-600 italic"}`}>{(c.name || "").trim() || "(No name)"}</p>
                    <p className="text-xs text-slate-500">{c.designation || c.type}{c.company ? ` · ${c.company}` : ""}</p>
                    {c.event_name && (
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100" data-testid={`contact-event-${c.id}`}>{c.event_name}</span>
                        <button onClick={(e) => { e.stopPropagation(); toggleCalled(c); }} title={c.followup_called_at ? `Called · ${c.followup_called_by_name || ""} ${new Date(c.followup_called_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} — click to undo` : "Mark as called"}
                          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${c.followup_called_at ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900 hover:text-slate-900"}`}
                          data-testid={`contact-called-${c.id}`}>
                          <PhoneCall className="h-3 w-3" /> {c.followup_called_at ? "Called" : "Mark called"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isGlobal && <DivisionTag id={c.division_id} />}
                  {canDelete(user, c) && (
                    <DeleteButton testId={`contact-delete-${c.id}`} title="Delete this contact?" description="This permanently removes the contact." onConfirm={() => del(c.id)} />
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{c.type}</span>
                {c.role_tag && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{c.role_tag}</span>}
                {c.marketing_opt_out && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium border border-red-100" data-testid={`contact-optout-${c.id}`}>Do not email{c.marketing_opt_out_reason ? ` · ${c.marketing_opt_out_reason}` : ""}</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                {c.phone && <p className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="h-3 w-3" /> {c.phone}</p>}
                {c.email && <p className="flex items-center gap-1.5 text-xs text-slate-500"><Mail className="h-3 w-3" /> {c.email}</p>}
              </div>
            </Card>
          ))}
          {contacts.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No contacts yet.</p>}
        </div>
      )}

      <ContactDialog open={open} onOpenChange={setOpen} initial={editing} dd={dd} isGlobal={isGlobal} onSaved={() => qc.invalidateQueries({ queryKey: ["contacts"] })} />
      <AddToBrevoDialog open={brevoOpen} onOpenChange={setBrevoOpen} contactIds={sel} onDone={() => setSel([])} />
    </div>
  );
}
